import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function LoanPage() {
    const [amount, setAmount] = useState(15000);
    const [rate, setRate] = useState(9.5);
    const [years, setYears] = useState(5);

    const { monthly, totalInterest, totalPaid } = useMemo(() => {
        const m = monthlyPayment(toNum(amount), toNum(rate), Math.max(0.5, toNum(years)));
        const totalPaid = m * toNum(years) * 12;
        return { monthly: m, totalInterest: totalPaid - toNum(amount), totalPaid };
    }, [amount, rate, years]);

    return (
        <CalculatorShell
            slug="loan"
            explainer={
                                <>
                    <h2>How this loan calculator works</h2>
                    <p>
                        A personal loan calculator shows your monthly payment and total interest for a given
                        loan amount, rate, and term. Use it to compare loan offers side by side or to decide
                        how much you can comfortably afford to borrow.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Amortization formula:</strong> the same equation used for mortgages — your payment stays fixed while the interest-to-principal ratio shifts each month.</li>
                        <li><strong>APR vs. rate:</strong> APR includes fees while the stated rate does not — always use APR when comparing lenders.</li>
                        <li><strong>Loan term:</strong> the biggest lever after rate — a shorter term raises monthly payments but drastically cuts total interest.</li>
                        <li><strong>Total cost:</strong> calculated as payment × number of payments, giving you the full picture beyond the monthly number.</li>
                        <li><strong>Origination fees:</strong> sometimes deducted from the disbursed amount, meaning you receive less than the loan face value.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Get quotes from three or more lenders, including credit unions, before accepting any offer.</li>
                        <li>Avoid extending the term just to lower the payment — you'll pay significantly more in total interest.</li>
                        <li>Check for prepayment penalties before making extra payments or paying off early.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Loan amount" prefix="$" value={amount} onChange={setAmount} dataTestId="calc-input-amount" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.1} dataTestId="calc-input-rate" />
                        <Field label="Term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Monthly payment"
                        value={monthly}
                        format={(n) => fmtUSD(n)}
                        secondary={[
                            { label: "Total paid", value: totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                            { label: "Total interest", value: totalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalinterest" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
